import TextareaAutosize from 'react-textarea-autosize';
import { ChangeEvent, TextareaHTMLAttributes, forwardRef, useCallback, useMemo } from 'react';

import { cn } from 'styles';

export type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  /**
   * Enables the user to resize the textarea vertically
   */
  resize?: boolean;

  /**
   * Calculates the height automatically based on its content
   *
   * Default is **true**
   *
   * You can use min-h-x classes to ensure a minimum height if necessary
   */
  autosize?: boolean;

  onValueChange?: (v: string) => void;
};

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, resize, autosize = true, onChange, onValueChange, ...props }, ref) => {
    const Comp = useMemo(() => (autosize ? TextareaAutosize : 'textarea'), [autosize]);

    const handleChange = useCallback(
      (event: ChangeEvent<HTMLTextAreaElement>) => {
        onChange?.(event);
        onValueChange?.(event.target.value);
      },
      [onChange, onValueChange],
    );

    return (
      <Comp
        ref={ref}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        {...(props as any)}
        onChange={handleChange}
        className={cn(
          'flex w-full rounded-md border border-input bg-background px-3 py-2 ring-offset-background',
          'text-sm small_tablet:text-base',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          'disabled:cursor-not-allowed disabled:opacity-50',
          'placeholder:text-xs placeholder:italic placeholder:text-muted-foreground/60',
          resize ? 'resize-y' : 'resize-none',
          'min-h-9',
          className,
        )}
      />
    );
  },
);

Textarea.displayName = 'Textarea';
